import InputTable from "components/InputTable";
import { StepProps } from "../Form";
import FormNav from "../FormNav";
import FormStyles from "../styled";
import RATINGS, { RatingConfig } from "constants/ratings";
import Rarity from "types/Rarity";
import styled from "styled-components";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import ConfigStorage from "storage/config";
import { NotificationManager } from "react-notifications";
import Button from "components/Button";
import useConfigStorage from "storage/config";

const StyledGroup = styled.div`
  margin: 16px;
  text-align: center;

  input {
    text-align: center;
  }

  label {
    > input {
      margin-left: 8px;
    }
  }
`;
const StyledGroupTitle = styled.div`
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 1.05rem;
  text-align: center;

  span {
    font-weight: 500;
    font-size: 0.9rem;
  }
`;

export type FilterConfig = Partial<{
  minEvents: number;
  minTeamBonus: number;
  minLvl5: number;
}>;

type OwnProps = {
  onRatingChange: (rating: RatingConfig) => any;
  onFilterChange: (filter: FilterConfig) => any;
};

type Props = StepProps & OwnProps;

const ChooseConfig = (props: Props) => {
  const [skills, setSkills] = useState(RATINGS.skills);
  const [resolve, setResolve] = useState(RATINGS.resolve);
  const [rarities, setRarities] = useState(RATINGS.rarities);
  const [comboEvent, setComboEvent] = useState(RATINGS.comboEvent);
  const [bonusTeam, setBonusTeam] = useState(RATINGS.bonusTeam);
  const [statSynergy, setStatSynergy] = useState(RATINGS.statSynergy);
  const [modified, setModified] = useState(false);

  const [minEvents, setMinEvents] = useState(0);
  const [minTeamBonus, setMinTeamBonus] = useState(0);
  const [minLvl5, setMinLvl5] = useState(0);

  const { getConfig, setConfig } = useConfigStorage();

  const updateConfig = async () => {
    const storageConfig = await getConfig();

    if (storageConfig) {
      if (storageConfig.bonusTeam) {
        setBonusTeam(storageConfig.bonusTeam);
      }

      if (storageConfig.comboEvent) {
        setComboEvent(storageConfig.comboEvent);
      }

      if (storageConfig.rarities) {
        const rarities = {
          UR: storageConfig.rarities.UR,
          SSR: storageConfig.rarities.SSR,
          SR: storageConfig.rarities.SR,
          R: storageConfig.rarities.R,
          N: storageConfig.rarities.N,
        };

        setRarities(rarities);
      }

      if (storageConfig.skills) {
        const skills = {
          UR: storageConfig.skills.UR,
          SSR: storageConfig.skills.SSR,
          SR: storageConfig.skills.SR,
          R: storageConfig.skills.R,
          N: storageConfig.skills.N,
        };

        setSkills(skills);
      }

      if (storageConfig.resolve) {
        setResolve(storageConfig.resolve);
      }

      if (storageConfig.statSynergy) {
        setStatSynergy(storageConfig.statSynergy);
      }
    }
  };

  useEffect(() => {
    updateConfig();
  }, []);

  const skillsInputs = Object.values(skills);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setModified(true);

    const setFunctions = {
      comboEvent: setComboEvent,
      bonusTeam: setBonusTeam,
      statSynergy: setStatSynergy,
      minEvents: setMinEvents,
      minTeamBonus: setMinTeamBonus,
      minLvl5: setMinLvl5,
    };

    const setFunction = setFunctions[name];
    setFunction(+value as number);
  };

  const onClick = async () => {
    try {
      await setConfig({ skills, resolve, rarities, comboEvent, bonusTeam, statSynergy });

      if (modified) {
        NotificationManager.success("Config updated!");
      }
    } catch (e) {
      NotificationManager.error(`Couldn't update config`);
      console.log(e);
    }

    props.onFilterChange({ minEvents, minLvl5, minTeamBonus });
    props.onRatingChange({ skills, resolve, rarities, comboEvent, bonusTeam, statSynergy });
  };

  const onSkillsChange = (values: number[][]) => {
    const newSkills = {
      [Rarity.UR]: values[0],
      [Rarity.SSR]: values[1],
      [Rarity.SR]: values[2],
      [Rarity.R]: values[3],
      [Rarity.N]: values[4],
    };

    setSkills(newSkills);
    setModified(true);
  };

  const onResolveChange = (values: number[][]) => {
    setResolve(values[0]);
    setModified(true);
  };

  const onRaritiesChange = (values: number[][]) => {
    const newRarities = {
      [Rarity.UR]: values[0][0],
      [Rarity.SSR]: values[0][1],
      [Rarity.SR]: values[0][2],
      [Rarity.R]: values[0][3],
      [Rarity.N]: values[0][4],
    };

    setRarities(newRarities);
    setModified(true);
  };

  const resetConfig = () => {
    setSkills(RATINGS.skills);
    setResolve(RATINGS.resolve);
    setRarities(RATINGS.rarities);
    setComboEvent(RATINGS.comboEvent);
    setBonusTeam(RATINGS.bonusTeam);
    setStatSynergy(RATINGS.statSynergy);
    setMinEvents(0);
    setMinTeamBonus(0);
    setMinLvl5(0);

    NotificationManager.info("Config has been reset!");
  };

  const ResetButton = () => (
    <div onClick={resetConfig}>
      <Button>Reset config</Button>
    </div>
  );

  return (
    <FormStyles.Step>
      <FormStyles.StepContent>
        <FormStyles.ConfigForm>
          <p style={{ textAlign: "center" }}>Config will be saved after clicking on the "Next" button</p>

          <StyledGroup>
            <StyledGroupTitle style={{ fontSize: "1.2rem" }}>Filters</StyledGroupTitle>

            <StyledGroup>
              <label>
                Minimum Lv. 5 Skills
                <input type="number" value={minLvl5} name="minLvl5" onChange={onInputChange} />
              </label>
            </StyledGroup>

            <StyledGroup>
              <label>
                Minimum Trainers with Team Bonus
                <input type="number" value={minTeamBonus} name="minTeamBonus" onChange={onInputChange} />
              </label>
            </StyledGroup>

            <StyledGroup>
              <label>
                Minimum Combo Events
                <input type="number" value={minEvents} name="minEvents" onChange={onInputChange} />
              </label>
            </StyledGroup>
          </StyledGroup>

          <StyledGroup>
            <StyledGroupTitle style={{ fontSize: "1.2rem" }}>Rating Config</StyledGroupTitle>
            <StyledGroup>
              <StyledGroupTitle>Skills</StyledGroupTitle>
              <InputTable
                inputs={skillsInputs}
                colNames={[1, 2, 3, 4, 5].map((i) => `Lv. ${i}`)}
                rowNames={Object.keys(Rarity)}
                onChange={onSkillsChange}
              ></InputTable>
            </StyledGroup>

            <StyledGroup>
              <StyledGroupTitle>Resolve</StyledGroupTitle>
              <InputTable
                inputs={[resolve]}
                colNames={[1, 2, 3, 4, 5].map((i) => `Lv. ${i}`)}
                rowNames={[]}
                onChange={onResolveChange}
              ></InputTable>
            </StyledGroup>

            <StyledGroup>
              <StyledGroupTitle>Trainers Rarity</StyledGroupTitle>
              <InputTable
                inputs={[Object.values(rarities)]}
                colNames={Object.keys(Rarity)}
                rowNames={[]}
                onChange={onRaritiesChange}
              ></InputTable>
            </StyledGroup>

            <StyledGroup>
              <StyledGroupTitle>Combo Event</StyledGroupTitle>
              <label>
                Points per Combo Event
                <input type="number" value={comboEvent} name="comboEvent" onChange={onInputChange} />
              </label>
            </StyledGroup>

            <StyledGroup>
              <StyledGroupTitle>Team Bonus</StyledGroupTitle>
              <label>
                Points per Trainer with Team Bonus
                <input type="number" value={bonusTeam} name="bonusTeam" onChange={onInputChange} />
              </label>
            </StyledGroup>

            <StyledGroup>
              <StyledGroupTitle>
                Stat Synergy <span>(e.g. 2 STR - 3 MNT - 1 Skill Points)</span>
              </StyledGroupTitle>
              <label>
                Points per Synergy
                <input type="number" value={statSynergy} name="statSynergy" onChange={onInputChange} />
              </label>
            </StyledGroup>
          </StyledGroup>
        </FormStyles.ConfigForm>
      </FormStyles.StepContent>
      <FormNav {...props} nextClick={onClick} buttons={[<ResetButton key={1} />]} />
    </FormStyles.Step>
  );
};

export default ChooseConfig;
